import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import "../Styles/SalesReport.css";

const API_URL = process.env.REACT_APP_API_URL;

const SalesReport = () => {
  // State for date-range reports
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // State for the customer-specific report
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({ code: "", name: "" });
  const [customerReportLoading, setCustomerReportLoading] = useState(false);
  const [customerReportError, setCustomerReportError] = useState("");
  const [customerReportStartDate, setCustomerReportStartDate] = useState("");
  const [customerReportEndDate, setCustomerReportEndDate] = useState("");


  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${API_URL}/customers`, { headers });
        setCustomers(response.data);
      } catch (err) {
        console.error("Failed to fetch customers:", err);
        setCustomerReportError("Could not load the customer list.");
      }
    };
    fetchCustomers();
  }, [headers]);

  const ensureDates = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return false;
    }
    setError("");
    return true;
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (endpoint, filenamePrefix) => {
    if (!ensureDates()) return;
    setLoading(true);
    try {
      const resp = await axios.get(
        `${API_URL}${endpoint}?start_date=${startDate}&end_date=${endDate}`,
        { headers, responseType: "blob" }
      );
      downloadBlob(new Blob([resp.data]), `${filenamePrefix}_${startDate}_to_${endDate}.xlsx`);
    } catch (err) {
      console.error("Download failed:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to generate report. There might be no data for this date range."
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadCustomerInvoices = async () => {
    if (!selectedCustomer.code) {
      setCustomerReportError("Please select a customer from the list.");
      return;
    }
    // Date validation for this specific report
    if ((customerReportStartDate && !customerReportEndDate) || (!customerReportStartDate && customerReportEndDate)) {
        setCustomerReportError("Please select both a start and end date, or leave both empty.");
        return;
    }

    setCustomerReportError("");
    setCustomerReportLoading(true);
    try {
      let url = `${API_URL}/reports/sales-by-invoice-by-customer/export?customercode=${selectedCustomer.code}`;
      if (customerReportStartDate && customerReportEndDate) {
          url += `&start_date=${customerReportStartDate}&end_date=${customerReportEndDate}`;
      }
        
      const resp = await axios.get(url, { headers, responseType: "blob" });
      
      const safeName = selectedCustomer.name.replace(/[^a-z0-9]/gi, '_');
      const dateSuffix = customerReportStartDate ? `_${customerReportStartDate}_to_${customerReportEndDate}` : "";
      downloadBlob(new Blob([resp.data]), `${safeName}_Invoice_Report${dateSuffix}.pdf`);
        } catch (err) {
       console.error("Customer invoice report download failed:", err);
       setCustomerReportError(
         err.response?.data?.detail ||
           "Failed to generate the report for this customer."
       );
    } finally {
      setCustomerReportLoading(false);
    }
  };

  const handleCustomerChange = (e) => {
      const code = e.target.value;
      const name = e.target.options[e.target.selectedIndex].text;
      setSelectedCustomer({ code, name });
  };

  const handleDownloadSales = () => handleDownload(`/reports/sales`, "SalesReport");
  const handleDownloadGst = () => handleDownload(`/reports/gst-summary/export`, "GST_Summary");
  const handleDownloadCustomerSales = () => handleDownload(`/reports/sales-by-customer/export`, "SalesByCustomer");
  const handleDownloadProductSales = () => handleDownload(`/reports/sales-by-product/export`, "SalesByProduct");
  const handleDownloadCustomerProfit = () => handleDownload(`/reports/profit-by-customer/export`, "ProfitByCustomer");
  const handleDownloadProductProfit = () => handleDownload(`/reports/profit-by-product/export`, "ProfitByProduct");

  return (
     <div className="sales-report-scroll-wrapper">
      <div className="report-container">
        <div className="report-card">
          <div className="card-header">
            <h3>📊 Download Reports by Date Range</h3>
            <p>Select a date range to download general sales reports.</p>
          </div>
          <div className="card-body">
            {error && <p className="error-message">{error}</p>}
            <div className="date-picker-grid">
              <div className="form-group">
                <label htmlFor="start-date">Start Date</label>
                <input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="end-date">End Date</label>
                <input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div className="buttons-grid">
              <button className="btn btn-primary" onClick={handleDownloadSales} disabled={loading}>Sales (Line Items)</button>
              <button className="btn" onClick={handleDownloadGst} disabled={loading}>GST Summary</button>
              <button className="btn" onClick={handleDownloadCustomerSales} disabled={loading}>Customer-wise Sales</button>
              <button className="btn" onClick={handleDownloadProductSales} disabled={loading}>Product-wise Sales</button>
              <button className="btn btn-success" onClick={handleDownloadCustomerProfit} disabled={loading}>Customer-wise Profit</button>
              <button className="btn btn-success" onClick={handleDownloadProductProfit} disabled={loading}>Product-wise Profit</button>
            </div>
          </div>
          <div className="card-footer" />
        </div>
      </div>
      
      <div className="report-container">
        <div className="report-card">
          <div className="card-header">
            <h3>📄 Customer Invoice Report</h3>
            <p>Select a customer and an optional date range for an invoice summary.</p>
          </div>
          <div className="card-body">
             {customerReportError && <p className="error-message">{customerReportError}</p>}
             <div className="customer-report-grid">
                <div className="form-group">
                    <label htmlFor="customer-select">Customer</label>
                     <select id="customer-select" value={selectedCustomer.code} onChange={handleCustomerChange}>
                        <option value="" disabled>-- Select a Customer --</option>
                        {customers.map(c => (
                            <option key={c.customercode} value={c.customercode}>{c.customername}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="customer-start-date">Start Date (Optional)</label>
                    <input id="customer-start-date" type="date" value={customerReportStartDate} onChange={(e) => setCustomerReportStartDate(e.target.value)} />
                </div>
                <div className="form-group">
                    <label htmlFor="customer-end-date">End Date (Optional)</label>
                    <input id="customer-end-date" type="date" value={customerReportEndDate} onChange={(e) => setCustomerReportEndDate(e.target.value)} />
                </div>
             </div>
             <div className="customer-report-controls">
                <button 
                    className="btn btn-primary" 
                    onClick={handleDownloadCustomerInvoices} 
                    disabled={customerReportLoading || !selectedCustomer.code}
                >
                    {customerReportLoading ? "Generating..." : "Download Invoice Report"}
                </button>
            </div>
          </div>
          <div className="card-footer" />
        </div>
      </div>
    </div>
  );
};

export default SalesReport;